import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Share2, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../App';
import { SafeHtml } from '../components/SafeHtml';
import { SubscribeBox } from './Newsletters';

const getYouTubeEmbed = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

export default function NewsletterDetail() {
  const { newsletterId } = useParams();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/newsletters/${newsletterId}`)
      .then(d => setNewsletter(d.newsletter))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [newsletterId]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 space-y-4">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!newsletter) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <p className="text-lg mb-4">Newsletter not found</p>
        <Link to="/newsletters"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Newsletters</Button></Link>
      </div>
    );
  }

  const ytEmbed = getYouTubeEmbed(newsletter.video_url);

  return (
    <div className="overflow-hidden bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/newsletters" className="inline-flex items-center text-sm text-[hsl(var(--lux-ink)/0.6)] hover:text-[hsl(var(--lux-royal))] mb-6" data-testid="newsletter-back-link">
          <ArrowLeft className="w-4 h-4 mr-1" /> All Newsletters
        </Link>

        <div className="flex items-center gap-2 text-sm text-[hsl(var(--lux-ink)/0.6)] mb-3">
          <CalendarDays className="w-4 h-4" />
          {newsletter.published_at ? new Date(newsletter.published_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
        </div>

        <h1 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--lux-royal))] mb-4" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="newsletter-detail-title">
          {newsletter.title}
        </h1>
        {/* Share buttons */}
        <div className="flex items-center gap-2 mb-6" data-testid="newsletter-share-buttons">
          <span className="text-xs text-[hsl(var(--lux-ink)/0.6)] flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> Share:</span>
          <Button size="sm" variant="outline" className="h-8 text-green-700 border-green-300 hover:bg-green-50"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${newsletter.title} — Gujarat Samaj Nottingham Newsletter: ${window.location.href}`)}`, '_blank')}
            data-testid="newsletter-share-whatsapp">
            <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
          </Button>
          <Button size="sm" variant="outline" className="h-8"
            onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent(newsletter.title + ' — Gujarat Samaj Nottingham')}&body=${encodeURIComponent('Read our latest newsletter: ' + window.location.href)}`; }}
            data-testid="newsletter-share-email">
            <Mail className="w-3.5 h-3.5 mr-1" /> Email
          </Button>
        </div>
        <div className="lux-gold-divider max-w-xs mb-8" />

        {newsletter.cover_image_url && (
          <img src={newsletter.cover_image_url} alt={newsletter.title} className="w-full max-h-[420px] object-cover rounded-2xl mb-8 shadow-lg" />
        )}

        <SafeHtml
          className="prose prose-lg max-w-none mb-10 [&_img]:rounded-xl [&_img]:shadow-md [&_a]:text-[hsl(var(--lux-royal))]"
          html={newsletter.content_html}
          data-testid="newsletter-detail-content"
        />

        {newsletter.video_url && (
          <div className="mb-12 rounded-2xl overflow-hidden shadow-lg" data-testid="newsletter-detail-video">
            {ytEmbed ? (
              <iframe
                src={ytEmbed}
                title="Newsletter video"
                className="w-full aspect-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video controls className="w-full">
                <source src={newsletter.video_url} />
                Your browser does not support video playback.
              </video>
            )}
          </div>
        )}

        <SubscribeBox />
      </div>
    </div>
  );
}
